import { NavLink } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@app/hooks";
import { toggleNavCollapsed } from "@app/slices/prefsSlice";
import { useLocale } from "@hooks/useLocale";
import type { MessageKey } from "@i18n/messages";
import { WellSwitcher } from "./WellSwitcher";
import { SettingsMenu } from "./SettingsMenu";

/** A W0–W9 nav entry. `icon` is a kit `ic-*` class (glyph mask, no icon dep). */
interface NavItem {
  to: string;
  labelKey: MessageKey;
  icon: string;
}

/** The sitemap — W0 finder first, then the authoring screens W1–W9. */
const ITEMS: NavItem[] = [
  { to: "/finder", labelKey: "nav.finder", icon: "ic-search" }, // W0
  { to: "/workspace", labelKey: "nav.workspace", icon: "ic-afe" }, // W1
  { to: "/hole-plan", labelKey: "nav.holePlan", icon: "ic-bha" }, // W2
  { to: "/casing", labelKey: "nav.casing", icon: "ic-tubulars" }, // W3
  { to: "/cost", labelKey: "nav.cost", icon: "ic-cost" }, // W4
  { to: "/program", labelKey: "nav.program", icon: "ic-daily" }, // W5
  { to: "/trajectory", labelKey: "nav.trajectory", icon: "ic-surveys" }, // W6
  { to: "/timeline", labelKey: "nav.timeline", icon: "ic-planner" }, // W7
  { to: "/versions", labelKey: "nav.versions", icon: "ic-saved" }, // W8
  { to: "/publish", labelKey: "nav.publish", icon: "ic-export" }, // W9
];

/**
 * Left nav — the full app chrome (the P3 rail). Hosts the W brand chip, the
 * well/pad switcher, the W0–W9 sitemap links, a collapse toggle, and (pinned to
 * the bottom) the Settings cog. Expanded shows icon + label; collapsed (rail)
 * shows the icon only with a hover tooltip. Collapsed state is persisted.
 */
export function LeftNav() {
  const { t } = useLocale();
  const dispatch = useAppDispatch();
  const collapsed = useAppSelector((s) => s.prefs.navCollapsed);

  return (
    <nav className="nav" aria-label="Primary" data-collapsed={collapsed}>
      <div className="brand">
        <div className="chip" aria-hidden="true">
          W
        </div>
        {!collapsed && <span className="wordmark-text">{t("app.name")}</span>}
      </div>

      <WellSwitcher collapsed={collapsed} />

      <div className="nav-links">
        {ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) => (isActive ? "active" : "")}
            title={t(item.labelKey)}
          >
            <span className={`ic ${item.icon}`} aria-hidden="true" />
            {!collapsed && <span className="nav-label">{t(item.labelKey)}</span>}
          </NavLink>
        ))}
      </div>

      <div className="spacer" />

      <button
        type="button"
        className="nav-collapse-toggle"
        aria-expanded={!collapsed}
        aria-label={collapsed ? t("nav.expand") : t("nav.collapse")}
        title={collapsed ? t("nav.expand") : t("nav.collapse")}
        onClick={() => dispatch(toggleNavCollapsed())}
      >
        <span className="chev" aria-hidden="true">
          {collapsed ? "»" : "«"}
        </span>
        {!collapsed && <span className="nav-label">{t("nav.collapse")}</span>}
      </button>

      <SettingsMenu collapsed={collapsed} />
    </nav>
  );
}
